export type AssistantActionType = "expense" | "income";

export interface ParsedFinancialCommand {
  type: AssistantActionType;
  amount: number;
  categoryHint: string | null;
  accountHint?: string | null;
  dateHint?: string | null;
  description: string;
  originalText: string;
}

interface ParseFinancialCommandOptions {
  categoryNames?: string[];
  accountNames?: string[];
}

const incomeVerbs = ["recebi", "ganhei", "entrou", "caiu", "depositei", "veio", "recebi de volta"];
const expenseVerbs = ["gastei", "paguei", "comprei", "debitei", "usei", "transferi", "enviei", "mandei", "saquei", "retirei", "cobrou", "deduziu"];

const categoryKeywords: Array<{ category: string; keywords: string[] }> = [
  { category: "transporte", keywords: ["uber", "99", "taxi", "táxi", "combustivel", "combustível", "gasolina", "onibus", "ônibus", "metro", "metrô"] },
  { category: "alimentacao", keywords: ["mercado", "supermercado", "ifood", "restaurante", "almoco", "almoço", "jantar", "lanche", "padaria"] },
  { category: "moradia", keywords: ["aluguel", "condominio", "condomínio", "luz", "agua", "água", "internet", "energia"] },
  { category: "saude", keywords: ["farmacia", "farmácia", "medico", "médico", "consulta", "exame", "plano de saude", "plano de saúde"] },
  { category: "lazer", keywords: ["cinema", "show", "netflix", "spotify", "viagem", "bar", "festa"] },
  { category: "salario", keywords: ["salario", "salário", "pagamento", "holerite"] },
  { category: "renda_extra", keywords: ["freela", "freelance", "bonus", "bônus", "comissao", "comissão", "pix recebido", "pix"] },
];

const stopTokens = new Set(["no", "na", "de", "do", "da", "em", "pro", "pra", "para"]);

function normalizeText(text: string) {
  return text
    .toLowerCase()
    .replace(/\bpics\b/g, "pix")
    .replace(/\bpiks\b/g, "pix")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function formatDescription(raw: string) {
  const cleaned = raw
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^[^a-z0-9]+/i, "")
    .replace(/[^a-z0-9]+$/i, "");

  if (!cleaned) return "";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function detectType(normalized: string): AssistantActionType | null {
  if (incomeVerbs.some((verb) => normalized.includes(verb))) return "income";
  if (expenseVerbs.some((verb) => normalized.includes(verb))) return "expense";
  return null;
}

function extractAmount(normalized: string): number | null {
  const match = normalized.match(/(\d+(?:[.,]\d{1,2})?)/);
  if (!match) return null;
  const value = Number(match[1].replace(",", "."));
  if (Number.isNaN(value) || value <= 0) return null;
  return value;
}

function extractExplicitCategoryHint(normalized: string): string | null {
  const match = normalized.match(/\bcategoria(?:\s*[:=-]\s*|\s+(?:e|eh)\s+|\s+)([a-z0-9\s]+)/);
  if (!match?.[1]) return null;
  return match[1].trim();
}

function detectCategoryHint(normalized: string, options?: ParseFinancialCommandOptions): string | null {
  const explicitCategory = extractExplicitCategoryHint(normalized);
  if (explicitCategory) return explicitCategory;

  const normalizedCategoryNames = (options?.categoryNames || [])
    .map((name) => normalizeText(name))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  for (const categoryName of normalizedCategoryNames) {
    if (normalized.includes(categoryName)) {
      return categoryName;
    }
  }

  for (const entry of categoryKeywords) {
    if (entry.keywords.some((keyword) => normalized.includes(normalizeText(keyword)))) {
      return entry.category;
    }
  }
  return null;
}

function detectAccountHint(normalized: string, options?: ParseFinancialCommandOptions): string | null {
  const normalizedAccountNames = (options?.accountNames || [])
    .map((name) => normalizeText(name))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  for (const accountName of normalizedAccountNames) {
    // Procura o nome da conta precedido opcionalmente por algumas preposições/palavras-chave comuns
    const regex = new RegExp(`(?:no|na|do|da|pelo|pela|conta|cartao|cartão)\\s*(?:de|do|da)?\\s*${accountName}\\b`);
    if (regex.test(normalized) || normalized.includes(accountName)) {
      return accountName;
    }
  }

  return null;
}

function detectDateHint(normalized: string): string | null {
  // Padrões de data relativa
  const relativeMatch = normalized.match(/\b(hoje|ontem|anteontem|amanha|amanhã)\b/);
  if (relativeMatch) {
    return relativeMatch[1];
  }

  // Padrões de data explícita com "dia X do Y" (comum na transcrição de voz)
  const voiceDateMatch = normalized.match(/\bdia\s+(\d{1,2})\s+(?:do|de)\s+(?:mes\s+)?(\d{1,2}|janeiro|fevereiro|marco|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\b/);
  if (voiceDateMatch) {
    return voiceDateMatch[0]; // Retorna a string inteira capturada, ex: "dia 22 do 03"
  }

  // Padrão "dia X" (assume mês atual)
  const simpleVoiceDateMatch = normalized.match(/\bdia\s+(\d{1,2})\b/);
  if (simpleVoiceDateMatch) {
    return simpleVoiceDateMatch[0];
  }

  // Padrão numérico clássico "DD/MM" ou "DD/MM/AAAA"
  const numericDateMatch = normalized.match(/\b(\d{1,2})[./](\d{1,2})(?:[./](\d{2,4}))?\b/);
  if (numericDateMatch) {
    return numericDateMatch[0];
  }

  return null;
}

function extractDescription(original: string, normalized: string, accountHint?: string | null, dateHint?: string | null): string {
  let cleaned = normalized;

  // Se detectamos uma conta, tentamos remover trechos relacionados a ela
  if (accountHint) {
    const accountRegex = new RegExp(`(?:no|na|do|da|pelo|pela|conta|cartao|cartão)\\s*(?:de|do|da)?\\s*${accountHint}\\b`, "g");
    cleaned = cleaned.replace(accountRegex, " ");
    // Caso tenha sobrado só o nome da conta, removemos também
    cleaned = cleaned.replace(new RegExp(`\\b${accountHint}\\b`, "g"), " ");
    // Remove palavras soltas relacionadas se sobraram após a remoção
    cleaned = cleaned.replace(/\b(?:conta|cartao|cartão)\b/g, " ");
  }

  // Se detectamos uma data, removemos o trecho correspondente
  if (dateHint) {
    // Normalizamos a string da data primeiro (tirar espaços em volta etc)
    const escapedDateHint = dateHint.replace(/\//g, "[./]").replace(/\s+/g, "\\s+");
    const dateRegex = new RegExp(`(?:no\\s+)?(?:dia\\s+)?${escapedDateHint}\\b`, "g");
    cleaned = cleaned.replace(dateRegex, " ");
    // Limpa também palavras soltas se sobraram "dia" solto ou a própria string original se não deu match com o regex
    cleaned = cleaned.replace(new RegExp(`\\b${escapedDateHint}\\b`, "g"), " ");
    cleaned = cleaned.replace(/\b(?:no dia|dia)\b/g, " ");
  }

  // Tratamentos adicionais de resíduos de datas relativas
  cleaned = cleaned.replace(/\b(hoje|ontem|anteontem|amanha|amanhã)\b/g, " ");

  cleaned = cleaned
    .replace(/\bcategoria(?:\s*[:=-]\s*|\s+(?:e|eh)\s+|\s+)[a-z0-9\s]+/g, " ")
    .replace(/\b\d+(?:[.,]\d{1,2})?\b/g, " ")
    .replace(/\breais?\b/g, " ")
    .replace(/\br\s*\$\s*/g, " ")
    .replace(/\b(gastei|paguei|comprei|debitei|usei|recebi|ganhei|entrou)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const parts = cleaned.split(" ").filter((token) => token && !stopTokens.has(token));
  const compact = parts.join(" ");

  if (!compact) {
    return formatDescription(original);
  }

  return formatDescription(compact);
}

export function parseFinancialCommand(command: string, options?: ParseFinancialCommandOptions): ParsedFinancialCommand | null {
  const originalText = command.trim();
  const normalized = normalizeText(originalText);

  if (!normalized) return null;

  const type = detectType(normalized);
  const amount = extractAmount(normalized);

  if (!type || amount === null) return null;

  const categoryHint = detectCategoryHint(normalized, options);
  const accountHint = detectAccountHint(normalized, options);
  const dateHint = detectDateHint(normalized);
  
  const description = extractDescription(originalText, normalized, accountHint, dateHint);

  if (!description) return null;

  return {
    type,
    amount,
    categoryHint,
    accountHint,
    dateHint,
    description,
    originalText,
  };
}
